import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { agent } from 'services/service';
import { AttendanceCalendarResponse } from 'services/AttendanceTrackingService';
import { STATUS_LABELS } from './calendarShared';
import {
  getCalendarDefaults,
  mapCalendarDaysToRecords
} from './calendarApi';

export const useAttendanceCalendar = (
  epfNumber: string,
  rangeStart: Date,
  rangeEnd: Date
) => {
  const [data, setData] = useState<AttendanceCalendarResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fromKey = format(rangeStart, 'yyyy-MM-dd');
  const toKey = format(rangeEnd, 'yyyy-MM-dd');

  useEffect(() => {
    if (!epfNumber) {
      setData(null);
      setLoading(false);
      setError('Employee not found.');
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const response =
          await agent.AttendanceTrackingService.getEmployeeAttendanceCalendar(
            epfNumber,
            fromKey,
            toKey
          );

        if (!cancelled) {
          setData(response.data);
        }
      } catch {
        if (!cancelled) {
          setData(null);
          setError('Failed to load attendance calendar.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [epfNumber, fromKey, toKey]);

  return useMemo(() => {
    const defaults = data ? getCalendarDefaults(data) : null;

    return {
      recordsByDate: data
        ? mapCalendarDaysToRecords(data.days)
        : new Map<string, import('./calendarShared').DayRecord>(),
      employeeName: defaults?.employeeName ?? '',
      minimumOfficeDays: defaults?.minimumOfficeDays ?? 8,
      legendItems: defaults?.legendItems ?? [],
      statusLabels: defaults?.statusLabels ?? STATUS_LABELS,
      loading,
      error
    };
  }, [data, loading, error]);
};
